<script setup lang="ts">
import { ChevronRight, KeyRound, ShieldAlert, X } from '@lucide/vue'
import { computed, ref, watch } from 'vue'

import type { Tier } from '../api'
import type { CatalogFamily, CatalogKind } from '../catalog'

/**
 * Dialog de sélection des prompts de démonstration. Sections repliables par
 * type (une famille = un groupe, un type = une ligne dépliable contenant le
 * prompt à charger). Deux usages : « secrets » et « injection ».
 */
const props = defineProps<{
    open: boolean
    kind: CatalogKind
    families: CatalogFamily[]
}>()

const emit = defineEmits<{
    (e: 'update:open', v: boolean): void
    (e: 'select', payload: { text: string; tier?: Tier }): void
}>()

const expanded = ref<Set<string>>(new Set())

const meta = computed(() =>
    props.kind === 'secrets'
        ? {
              icon: KeyRound,
              title: 'Secrets & données sensibles',
              sub: 'Choisissez un type — PreLLM le tokenise avant l’envoi au fournisseur.',
          }
        : {
              icon: ShieldAlert,
              title: 'Injections de prompt',
              sub: 'Choisissez une attaque — PreLLM la bloque avant qu’elle n’atteigne le fournisseur.',
          },
)

function toggle(id: string): void {
    const next = new Set(expanded.value)
    next.has(id) ? next.delete(id) : next.add(id)
    expanded.value = next
}

function close(): void {
    emit('update:open', false)
}

function choose(text: string, tier?: Tier): void {
    emit('select', { text, tier })
    close()
}

function onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') close()
}

watch(
    () => props.open,
    (v) => {
        if (v) {
            expanded.value = new Set()
            window.addEventListener('keydown', onKeydown)
        } else {
            window.removeEventListener('keydown', onKeydown)
        }
    },
)
</script>

<template>
    <Teleport to="body">
        <Transition name="dlg">
            <div v-if="open" class="dlg" @click.self="close">
                <div class="dlg__panel" role="dialog" aria-modal="true">
                    <header class="dlg__head">
                        <div class="dlg__title-wrap">
                            <component :is="meta.icon" :size="18" :stroke-width="2" class="dlg__title-icon" />
                            <div>
                                <h2 class="dlg__title">{{ meta.title }}</h2>
                                <p class="dlg__sub">{{ meta.sub }}</p>
                            </div>
                        </div>
                        <button type="button" class="dlg__close" aria-label="Fermer" @click="close">
                            <X :size="18" :stroke-width="2" />
                        </button>
                    </header>

                    <div class="dlg__body">
                        <section v-for="family in families" :key="family.label" class="dlg__family">
                            <h3 class="dlg__family-title">{{ family.label }}</h3>
                            <ul class="dlg__types">
                                <li v-for="t in family.types" :key="t.id" class="type">
                                    <button
                                        type="button"
                                        class="type__head"
                                        :class="{ 'type__head--open': expanded.has(t.id) }"
                                        :aria-expanded="expanded.has(t.id)"
                                        @click="toggle(t.id)"
                                    >
                                        <ChevronRight :size="15" :stroke-width="2.2" class="type__chevron" />
                                        <span class="type__label">{{ t.label }}</span>
                                        <span class="type__hint">{{ t.hint }}</span>
                                    </button>
                                    <div v-if="expanded.has(t.id)" class="type__body">
                                        <p class="type__prompt">{{ t.prompt }}</p>
                                        <button type="button" class="type__use" @click="choose(t.prompt, t.tier)">
                                            Charger ce prompt
                                        </button>
                                    </div>
                                </li>
                            </ul>
                        </section>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<style scoped>
.dlg {
    position: fixed;
    inset: 0;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: var(--p-color-overlay);
}
.dlg__panel {
    width: 100%;
    max-width: 580px;
    max-height: 84vh;
    display: flex;
    flex-direction: column;
    background: var(--p-color-surface);
    border: 1px solid var(--p-color-line);
    border-radius: var(--p-radius-md);
    box-shadow: var(--p-shadow-modal);
    overflow: hidden;
}

.dlg__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 18px 20px;
    border-bottom: 1px solid var(--p-color-line);
}
.dlg__title-wrap {
    display: flex;
    gap: 12px;
    align-items: flex-start;
}
.dlg__title-icon {
    margin-top: 2px;
    color: var(--p-color-olive);
    flex-shrink: 0;
}
.dlg__title {
    margin: 0;
    font-family: var(--p-font-display);
    font-size: var(--p-text-lg);
    font-weight: var(--p-font-weight-medium);
    color: var(--p-color-ink);
}
.dlg__sub {
    margin: 2px 0 0;
    font-size: var(--p-text-xs);
    color: var(--p-color-ink-4);
    line-height: var(--p-line-normal);
}
.dlg__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: var(--p-radius-sm);
    border: 1px solid transparent;
    background: transparent;
    color: var(--p-color-ink-4);
    cursor: pointer;
    flex-shrink: 0;
    transition: background var(--p-motion-fast) var(--p-ease-out);
}
.dlg__close:hover {
    background: var(--p-color-surface-alt);
    color: var(--p-color-ink);
}

.dlg__body {
    overflow-y: auto;
    padding: 8px 20px 20px;
}
.dlg__family {
    margin-top: 16px;
}
.dlg__family-title {
    margin: 0 0 8px;
    font-family: var(--p-font-mono);
    font-size: var(--p-text-2xs);
    text-transform: uppercase;
    letter-spacing: var(--p-tracking-wider);
    color: var(--p-color-ink-4);
}
.dlg__types {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.type {
    border: 1px solid var(--p-color-line);
    border-radius: var(--p-radius-sm);
    background: var(--p-color-bg);
    overflow: hidden;
}
.type__head {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: left;
    transition: background var(--p-motion-fast) var(--p-ease-out);
}
.type__head:hover {
    background: var(--p-color-surface-alt);
}
.type__chevron {
    color: var(--p-color-ink-5);
    flex-shrink: 0;
    transition: transform var(--p-motion-fast) var(--p-ease-out);
}
.type__head--open .type__chevron {
    transform: rotate(90deg);
}
.type__label {
    font-size: var(--p-text-sm);
    color: var(--p-color-ink);
    font-weight: var(--p-font-weight-medium);
}
.type__hint {
    margin-left: auto;
    font-family: var(--p-font-mono);
    font-size: var(--p-text-2xs);
    color: var(--p-color-ink-5);
    text-align: right;
}
.type__body {
    padding: 4px 12px 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    animation: dlg-reveal var(--p-motion-fast) var(--p-ease-out);
}
@keyframes dlg-reveal {
    from {
        opacity: 0;
        transform: translateY(-2px);
    }
    to {
        opacity: 1;
        transform: none;
    }
}
.type__prompt {
    margin: 0;
    padding: 10px 12px;
    border: 1px solid var(--p-color-line);
    border-radius: var(--p-radius-sm);
    background: var(--p-color-surface);
    font-size: var(--p-text-sm);
    line-height: var(--p-line-relaxed);
    color: var(--p-color-ink-2);
    white-space: pre-wrap;
    word-break: break-word;
}
.type__use {
    align-self: flex-start;
    padding: 6px 14px;
    border-radius: var(--p-radius-sm);
    border: 1px solid var(--p-color-ink);
    background: var(--p-color-ink);
    color: var(--p-color-surface);
    font-family: var(--p-font-sans);
    font-size: var(--p-text-xs);
    cursor: pointer;
    transition: background var(--p-motion-fast) var(--p-ease-out);
}
.type__use:hover {
    background: var(--p-color-ink-2);
}

/* Transition d'ouverture */
.dlg-enter-active,
.dlg-leave-active {
    transition: opacity var(--p-motion-base) var(--p-ease-out);
}
.dlg-enter-from,
.dlg-leave-to {
    opacity: 0;
}
.dlg-enter-active .dlg__panel,
.dlg-leave-active .dlg__panel {
    transition: transform var(--p-motion-base) var(--p-ease-out);
}
.dlg-enter-from .dlg__panel,
.dlg-leave-to .dlg__panel {
    transform: translateY(8px) scale(0.98);
}
</style>
